import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  CircularProgress,
  Alert,
  SelectChangeEvent,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { api } from '../services/api';

interface Measurement {
  id: string;
  date: string;
  time: string;
  ecCoirBag: number;
  moistureVMC: number;
  coirBagTemp: number;
  airTemp: number;
  runOff: number;
  createdAt: string;
  updatedAt: string;
  zoneId: string;
}

interface Farm {
  id: string;
  name: string;
  user_id: string;
}

interface Zone {
  id: string;
  name: string;
  farm_id: string;
}

const ReportsPage: React.FC = () => {
  const { user } = useAuth();
  const [reportType, setReportType] = useState('daily');
  // Date range filter removed as requested
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [farms, setFarms] = useState<Farm[]>([]);
  const [selectedFarm, setSelectedFarm] = useState('');
  const [zones, setZones] = useState<Zone[]>([]);
  const [selectedZone, setSelectedZone] = useState('');
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [selectedMetric, setSelectedMetric] = useState('ecCoirBag');

  // Effect to load farms when user changes
  useEffect(() => {
    const fetchFarms = async () => {
      try {
        setLoading(true);
        setError('');
        if (!user?.id) {
          throw new Error('User not authenticated');
        }
        
        // Fetch all farms for the user
        const farmsResponse = await api.farms.getAll();
        if (farmsResponse.error) {
          throw new Error(farmsResponse.error);
        }
        
        // Safely check if data exists and has items
        const farmsData = farmsResponse.data || [];
        if (farmsData.length === 0) {
          throw new Error('No farms found for this user');
        }
        
        // Set farms and select the first one by default if none is selected
        setFarms(farmsData);
        if (!selectedFarm) {
          setSelectedFarm(farmsData[0].id);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchFarms();
  }, [user?.id]);

  // Effect to load zones when selected farm changes
  useEffect(() => {
    const fetchZones = async () => {
      if (!selectedFarm) return;
      
      try {
        setLoading(true);
        
        // Fetch zones for the selected farm
        const zonesResponse = await api.zones.getAll(selectedFarm);
        if (zonesResponse.error) {
          throw new Error(zonesResponse.error);
        }
        
        // Safely set zones data
        const zonesData = zonesResponse.data || [];
        setZones(zonesData);
        
        // Reset selected zone when farm changes
        setSelectedZone('');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchZones();
  }, [selectedFarm]);

  // Debug component to display state values
  useEffect(() => {
    console.group('=== DEBUG INFO ===');
    console.log('User:', user?.id);
    console.log('Selected Farm:', selectedFarm);
    console.log('Selected Zone:', selectedZone);
    console.log('Zones available:', zones.length, 'zones');
    console.log('Measurements:', measurements.length, 'measurements');
    console.log('Selected Metric:', selectedMetric);
    console.log('Loading state:', loading);
    console.log('Error state:', error || 'No errors');
    
    // Check if measurements have the expected properties
    if (measurements.length > 0) {
      console.group('First measurement data sample:');
      console.log(measurements[0]);
      console.log('Has selected metric?', selectedMetric in measurements[0]);
      console.log('Metric value:', measurements[0][selectedMetric as keyof Measurement]);
      console.groupEnd();
    }
    
    console.groupEnd();
  }, [user?.id, selectedFarm, selectedZone, zones, measurements, selectedMetric, loading, error]);

  // Helper function to transform measurement data into chart-friendly format
  const transformMeasurementData = (data: any[]): Measurement[] => {
    console.log('Transforming measurement data:', data);
    
    // If data is already in the expected format, return it
    if (data.length === 0) return [];
    
    // Check if the first item has the expected properties
    const firstItem = data[0];
    console.log('First item in data:', firstItem);
    
    // If the data already has the expected structure, return it as is
    if ('ecCoirBag' in firstItem && 'date' in firstItem) {
      console.log('Data already in expected format');
      return data as Measurement[];
    }
    
    // Otherwise, try to transform the data
    try {
      const transformed = data.map(item => {
        // Log the item to see its structure
        console.log('Transforming item:', item);
        
        // Create a measurement object with default values
        const measurement: Partial<Measurement> = {
          id: item.id || `temp-${Math.random().toString(36).substr(2, 9)}`,
          date: item.date || item.created_at || new Date().toISOString().split('T')[0],
          time: item.time || '',
          zoneId: item.zone_id || selectedZone || '',
          createdAt: item.created_at || new Date().toISOString(),
          updatedAt: item.updated_at || new Date().toISOString(),
        };
        
        // Map the metrics from the API response to our Measurement interface
        // This handles different naming conventions between API and frontend
        if ('ec_coir_bag' in item) measurement.ecCoirBag = Number(item.ec_coir_bag);
        else if ('ecCoirBag' in item) measurement.ecCoirBag = Number(item.ecCoirBag);
        else if ('ec' in item) measurement.ecCoirBag = Number(item.ec);
        
        if ('moisture_vmc' in item) measurement.moistureVMC = Number(item.moisture_vmc);
        else if ('moistureVMC' in item) measurement.moistureVMC = Number(item.moistureVMC);
        else if ('moisture' in item) measurement.moistureVMC = Number(item.moisture);
        
        if ('coir_bag_temp' in item) measurement.coirBagTemp = Number(item.coir_bag_temp);
        else if ('coirBagTemp' in item) measurement.coirBagTemp = Number(item.coirBagTemp);
        else if ('soil_temp' in item) measurement.coirBagTemp = Number(item.soil_temp);
        
        if ('air_temp' in item) measurement.airTemp = Number(item.air_temp);
        else if ('airTemp' in item) measurement.airTemp = Number(item.airTemp);
        else if ('temperature' in item) measurement.airTemp = Number(item.temperature);
        
        if ('run_off' in item) measurement.runOff = Number(item.run_off);
        else if ('runOff' in item) measurement.runOff = Number(item.runOff);
        
        return measurement as Measurement;
      });
      
      console.log('Transformed data:', transformed);
      return transformed;
    } catch (err) {
      console.error('Error transforming data:', err);
      return [];
    }
  };

  // Effect to load measurements when selected zone changes
  useEffect(() => {
    const fetchMeasurements = async () => {
      try {
        setLoading(true);
        console.log(`Fetching measurements for zone: "${selectedZone || 'all zones'}"`); 
        
        // Fetch measurements for the selected zone (if any)
        try {
          // Add timestamp to prevent caching
          const timestamp = new Date().getTime();
          const measurementsData = await api.measurements.getAll(selectedZone || '');
          console.log(`[${timestamp}] Raw measurements API response:`, measurementsData);
          
          // Process the response data
          let processedData: any[] = [];
          
          // Check if the response is an array or has a data property
          if (Array.isArray(measurementsData)) {
            console.log(`Found ${measurementsData.length} measurements as array`);
            processedData = measurementsData;
          } else if (measurementsData && typeof measurementsData === 'object') {
            // If it has a data property, use that
            if ('data' in measurementsData && Array.isArray(measurementsData.data)) {
              console.log(`Found ${measurementsData.data.length} measurements in data property`);
              processedData = measurementsData.data;
            } else {
              // If it's an object but not in the expected format, try to extract data
              console.warn('Unexpected measurements data format:', measurementsData);
              console.log('Object keys:', Object.keys(measurementsData));
              
              // Try to find any array property that might contain the data
              const arrayProps = Object.entries(measurementsData)
                .filter(([_, value]) => Array.isArray(value))
                .map(([key, value]) => ({ key, length: (value as any[]).length }));
              
              console.log('Potential array properties:', arrayProps);
              
              if (arrayProps.length > 0) {
                // Use the largest array found
                const largestArray = arrayProps.reduce((prev, curr) => 
                  prev.length > curr.length ? prev : curr
                );
                console.log(`Using array property '${largestArray.key}' with ${largestArray.length} items`);
                processedData = measurementsData[largestArray.key];
              }
            }
          } else {
            console.warn('Invalid measurements data type:', typeof measurementsData);
          }
          
          // Transform the data to ensure it's in the right format for the chart
          const transformedData = transformMeasurementData(processedData);
          setMeasurements(transformedData);
          
        } catch (apiErr) {
          console.error('Error fetching measurements:', apiErr);
          console.error('Error details:', apiErr instanceof Error ? apiErr.message : 'Unknown error');
          setMeasurements([]);
          throw apiErr;
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if we have a selected zone
    if (selectedZone) {
      fetchMeasurements();
      
      // Set up polling for live updates
      const interval = setInterval(fetchMeasurements, 30000); // Update every 30 seconds
      return () => clearInterval(interval);
    } else {
      setMeasurements([]);
    }
  }, [selectedZone]);

  const handleReportTypeChange = (event: SelectChangeEvent<string>) => {
    setReportType(event.target.value);
  };

  const handleFarmChange = (event: SelectChangeEvent<string>) => {
    setSelectedFarm(event.target.value);
  };

  const handleZoneChange = (event: SelectChangeEvent<string>) => {
    setSelectedZone(event.target.value);
  };

  const handleMetricChange = (event: SelectChangeEvent<string>) => {
    setSelectedMetric(event.target.value);
  };

  // Date range filter removed as requested

  const metrics: { id: string; name: string }[] = [
    { id: 'ecCoirBag', name: 'EC Coir Bag (mS/cm)' },
    { id: 'moistureVMC', name: 'Moisture VMC (%)' },
    { id: 'coirBagTemp', name: 'Coir Bag Temp (°C)' },
    { id: 'airTemp', name: 'Air Temp (°C)' },
    { id: 'runOff', name: 'Run Off (%)' },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ mb: 4 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button variant="contained" onClick={() => window.location.reload()}>
              Retry
            </Button>
          </Box>
        ) : (
          <Box>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h4" component="h1" fontWeight="bold">
                Live Reports & Analytics
              </Typography>
              <Button variant="contained" color="primary" href="/data-entry">
                Add New Entry
              </Button>
            </Box>
            <Paper sx={{ p: 4, mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Live Data Charts
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={3}>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Report Type</InputLabel>
                    <Select
                      value={reportType}
                      onChange={handleReportTypeChange}
                      label="Report Type"
                    >
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel id="farm-label">Farm</InputLabel>
                    <Select
                      labelId="farm-label"
                      id="farm"
                      value={selectedFarm}
                      label="Farm"
                      onChange={handleFarmChange}
                    >
                      {farms.map((farm) => (
                        <MenuItem key={farm.id} value={farm.id}>
                          {farm.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Zone</InputLabel>
                    <Select
                      value={selectedZone}
                      onChange={handleZoneChange}
                      label="Zone"
                      disabled={zones.length === 0}
                    >
                      <MenuItem value="">All Zones</MenuItem>
                      {zones.map((zone) => (
                        <MenuItem key={zone.id} value={zone.id}>
                          {zone.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel id="metric-label">Metric</InputLabel>
                    <Select
                      labelId="metric-label"
                      id="metric"
                      value={selectedMetric}
                      label="Metric"
                      onChange={handleMetricChange}
                    >
                      {metrics.map((metric) => (
                        <MenuItem key={metric.id} value={metric.id}>
                          {metric.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {/* Date range pickers removed as requested */}
              </Grid>
            </Paper>

            <Paper sx={{ p: 4, mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Live Data Charts
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Card sx={{ height: '100%' }}>
                    <CardHeader title="Chart" />
                    <Divider />
                    <CardContent>
                      {/* Debug info for chart data */}
                      <Box sx={{ position: 'absolute', top: 10, right: 10, zIndex: 1, p: 1, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Data points: {measurements.length} | Metric: {selectedMetric}
                        </Typography>
                      </Box>
                      
                      <ResponsiveContainer width="100%" height={400}>
                        {measurements.length > 0 ? (
                          <LineChart
                            data={measurements}
                            margin={{
                              top: 20,
                              right: 30,
                              left: 20,
                              bottom: 5,
                            }}
                            onMouseEnter={() => {
                              console.group('Chart Data Analysis');
                              console.log('Chart data:', measurements);
                              console.log('Data count:', measurements.length);
                              if (measurements.length > 0) {
                                console.log('First data point:', measurements[0]);
                                console.log('Selected metric exists in data?', selectedMetric in measurements[0]);
                                console.log('Metric value type:', typeof measurements[0][selectedMetric as keyof Measurement]);
                              }
                              console.groupEnd();
                            }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Line 
                              type="monotone" 
                              dataKey={selectedMetric} 
                              name={metrics.find(m => m.id === selectedMetric)?.name || 'Selected Metric'} 
                              stroke="#8884d8" 
                            />
                          </LineChart>
                        ) : (
                          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column' }}>
                            <Typography variant="body1" color="text.secondary">
                              No measurement data available for the selected zone
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
                              Debug: Zone ID = {selectedZone || 'none'}
                            </Typography>
                          </Box>
                        )}
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardHeader title="Summary" />
                    <Divider />
                    <CardContent>
                      {measurements.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No data available
                        </Typography>
                      ) : (
                        <Box>
                          {metrics.map((metric) => {
                            const value = measurements[0][metric.id as keyof Measurement];
                            return (
                              <Typography key={metric.id} variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                <strong>{metric.name}:</strong> {value !== undefined ? value : 'N/A'} {value !== undefined ? (metric.id === 'moistureVMC' ? '%' : metric.id === 'coirBagTemp' || metric.id === 'airTemp' ? '°C' : 'mS/cm') : ''}
                              </Typography>
                            );
                          })}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Min / Max
              </Typography>
              <Typography variant="body1">
                {measurements.length > 0
                  ? (() => {
                      // Extract numeric values, filtering out undefined/null/NaN
                      const values = measurements
                        .map((item: Measurement) => {
                          const val = item[selectedMetric as keyof Measurement];
                          return val !== undefined && val !== null ? Number(val) : null;
                        })
                        .filter((val): val is number => val !== null && !isNaN(val));
                      
                      console.log(`Min/Max calculation - Valid values for ${selectedMetric}:`, values);
                      
                      if (values.length === 0) return 'No valid data for selected metric';
                      
                      return `${Math.min(...values).toFixed(2)} / ${Math.max(...values).toFixed(2)} (from ${values.length} data points)`;
                    })()
                  : 'No data available'}
              </Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              This is a placeholder for the reports page. In a real application, this would show actual data from your database.
            </Typography>
          </Box>
        )}
      </Container>
    </LocalizationProvider>
  );
};

export default ReportsPage;
